import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, getDocs, orderBy, deleteDoc, doc, getDoc, updateDoc, arrayRemove } from 'firebase/firestore';
import { db } from '../firebase';
import { Button, Spinner } from '../components/UI';
import GrowthChart from '../components/GrowthChart';
import ShareChildModal from '../components/ShareChildModal';
import ImmunizationScreen from './ImmunizationScreen';
import SharedNotesScreen from './SharedNotesScreen';
import ActionSheet from '../components/ActionSheet';
import InterventionPlanCard from '../components/InterventionPlanCard'; 
import { calculateAge } from '../helpers/utils';

const TabButton = ({ label, isActive, onClick, icon }) => (
    <button 
        onClick={onClick} 
        className={`flex-1 flex flex-col items-center justify-center py-4 px-2 relative transition-all duration-300
            ${isActive ? 'text-indigo-600' : 'text-gray-400 hover:text-indigo-400'}
        `}
    >
        <span className="text-xl mb-1">{icon}</span>
        <span className={`text-[10px] font-bold tracking-widest uppercase transition-all ${isActive ? 'opacity-100' : 'opacity-70'}`}>
            {label}
        </span>
        {isActive && (
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1/2 h-1 bg-indigo-600 rounded-t-full shadow-[0_-2px_10px_rgba(79,70,229,0.5)]"></div>
        )}
    </button>
);



const getAssessmentTypes = (assessment) => {
    const types = [];
    if (assessment.perkembangan) types.push('KPSP');
    if (assessment.mental) types.push('KMME');
    if (assessment.pertumbuhan) types.push('Fisik');
    if (assessment.dayaDengar) types.push('TDD'); 
    if (assessment.pemeriksaanMata) types.push('Mata'); 
    if (assessment.chat) types.push('CHAT');
    if (assessment.gpph) types.push('GPPH');
    return types.length > 0 ? types.join(', ') : 'Umum';
};

export default function ChildDetailScreen({ user, child, navigate, showToast }) {
    const [assessments, setAssessments] = useState([]);
    const [immunizations, setImmunizations] = useState({});
    const [activeIntervention, setActiveIntervention] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showShareModal, setShowShareModal] = useState(false);
    const [showChildActionSheet, setShowChildActionSheet] = useState(false);
    const [showHistoryActionSheet, setShowHistoryActionSheet] = useState(null);
    const [activeTab, setActiveTab] = useState('history');

    const fetchData = useCallback(async () => {
        if (!user || !child) return;
        setLoading(true);
        try {
            const basePath = `children/${child.id}`;

            const assessmentsPath = `${basePath}/assessments`;
            const qAssessments = query(collection(db, assessmentsPath), orderBy('createdAt', 'desc'));
            const assessmentSnapshot = await getDocs(qAssessments);
            setAssessments(assessmentSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

            const immunizationsRef = doc(db, `${basePath}/privateData/immunization`);
            const immunizationsSnap = await getDoc(immunizationsRef);
            if (immunizationsSnap.exists()) setImmunizations(immunizationsSnap.data());

            const plansPath = `${basePath}/interventionPlans`;
            const qPlans = query(collection(db, plansPath), orderBy('createdAt', 'desc'));
            const plansSnapshot = await getDocs(qPlans);
            if (!plansSnapshot.empty) {
                const activePlan = plansSnapshot.docs.map(d => d.data()).find(p => p.status === 'active');
                setActiveIntervention(activePlan);
            }

        } catch (error) {
            console.error("Error fetching data:", error);
            setError("Gagal memuat data anak. Pastikan Anda memiliki izin.");
        } finally {
            setLoading(false);
        }
    }, [user, child]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleRemoveAccessOrDelete = async () => {
        try {
            const childRef = doc(db, `children/${child.id}`);
            const childDoc = await getDoc(childRef);
            if (!childDoc.exists()) {
                navigate('home');
                return;
            }

            const roleField = user.role === 'Tenaga Kesehatan' ? 'nakes' : user.role === 'Guru PAUD' ? 'teachers' : 'parents';

            // Unlink access
            await updateDoc(childRef, {
                [roleField]: arrayRemove(user.uid)
            });

            // Re-fetch to check if completely abandoned (Hard Delete logic)
            const updatedDoc = await getDoc(childRef);
            if (updatedDoc.exists()) {
                const updatedData = updatedDoc.data();
                const parents = updatedData.parents || [];
                const nakes = updatedData.nakes || [];
                const teachers = updatedData.teachers || [];
                
                if (parents.length === 0 && nakes.length === 0 && teachers.length === 0) {
                    await deleteDoc(childRef);
                    console.log("Child fully deleted (abandoned by all).");
                }
            }

            showToast(child.isShared ? 'Akses ke data anak berhasil dihapus.' : 'Data anak berhasil dihapus.');
            navigate('home');
        } catch (error) {
            console.error("Error removing/deleting child:", error);
            showToast("Gagal memproses penghapusan.", "error");
        }
    };

    const handleDeleteAssessment = async (assessmentId) => {
        try {
            const assessmentPath = `children/${child.id}/assessments/${assessmentId}`;
            await deleteDoc(doc(db, assessmentPath));
            fetchData();
            showToast('Riwayat pemeriksaan berhasil dihapus.');
        } catch (error) {
            console.error("Error deleting assessment:", error);
            showToast("Gagal menghapus riwayat.", "error");
        }
    };

    const getChildActionOptions = () => {
        if (child.isShared) {
            return [
                { label: 'Hapus Akses Data Ini', isDestructive: true, action: handleRemoveAccessOrDelete },
            ];
        }
        return [
            { label: 'Edit Data Anak', action: () => navigate('editChild', child) },
            { label: 'Hapus Data Anak', isDestructive: true, action: handleRemoveAccessOrDelete },
        ];
    };

    const historyActionOptions = (assessmentId) => [
        { label: 'Hapus Riwayat Ini', isDestructive: true, action: () => handleDeleteAssessment(assessmentId) },
    ];

    const getStatusColor = (status) => {
        if (!status) return 'bg-gray-100 text-gray-600';
        if (status.includes('Sesuai') || status.includes('Normal') || status.includes('Rendah')) return 'bg-emerald-100 text-emerald-700';
        if (status.includes('Meragukan') || status.includes('Sedang')) return 'bg-amber-100 text-amber-700';
        return 'bg-rose-100 text-rose-700';
    };

    const renderTabContent = () => {
        switch (activeTab) {
            case 'chart':
                return assessments.length > 1 ? (
                    <div className="bg-white/60 backdrop-blur-2xl rounded-[1.5rem] p-5 border border-white shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
                        <GrowthChart assessments={assessments} childGender={child.gender} />
                    </div>
                ) : (
                    <div className="text-center py-10 px-6 bg-white/50 backdrop-blur-xl rounded-[2rem] border border-white/60 shadow-sm">
                        <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center text-3xl mx-auto mb-4 grayscale opacity-50">📈</div>
                        <h3 className="text-lg font-bold text-gray-800 mb-1">Data Belum Cukup</h3>
                        <p className="text-sm text-gray-500 leading-relaxed">Grafik pertumbuhan akan muncul setelah ada minimal 2 data pemeriksaan fisik.</p>
                    </div>
                );
            case 'immunization':
                return (
                    <div className="bg-white/60 backdrop-blur-2xl rounded-[1.5rem] p-5 border border-white shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
                        <ImmunizationScreen user={user} child={child} />
                    </div>
                );
            case 'notes':
                return (
                    <SharedNotesScreen user={user} child={child} />
                );
            case 'history':
            default:
                return (
                    <div className="space-y-4">
                        {loading ? (
                            <div className="flex justify-center py-10"><Spinner /></div>
                        ) : assessments.length === 0 ? (
                            <div className="text-center py-10 px-6 bg-white/50 backdrop-blur-xl rounded-[2rem] border border-white/60 shadow-sm">
                                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center text-3xl mx-auto mb-4 grayscale opacity-50">📭</div>
                                <h3 className="text-lg font-bold text-gray-800 mb-1">Belum Ada Riwayat</h3>
                                <p className="text-sm text-gray-500 leading-relaxed">Lakukan pemeriksaan pertama untuk memantau tumbuh kembang anak.</p>
                            </div>
                        ) : (
                            assessments.map(asmnt => (
                                <div key={asmnt.id} className="group bg-white/70 backdrop-blur-xl border border-white rounded-[1.5rem] p-4 shadow-[0_4px_20px_rgba(0,0,0,0.03)] flex items-center justify-between transition-all hover:shadow-[0_8px_30px_rgba(0,0,0,0.08)] hover:-translate-y-0.5">
                                    <div className="flex-1">
                                        <div className="flex items-center space-x-2 mb-1.5">
                                            <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold tracking-widest uppercase ${getStatusColor(asmnt.overallStatus)}`}>
                                                {asmnt.overallStatus || 'Belum Selesai'}
                                            </span>
                                            <span className="text-xs font-bold text-gray-400">
                                                {getAssessmentTypes(asmnt)}
                                            </span>
                                        </div>
                                        <h4 className="font-extrabold text-gray-800 text-[15px]">
                                            {asmnt.createdAt.toDate().toLocaleString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' })}
                                        </h4>
                                        <p className="text-xs text-gray-500 font-medium">
                                            {asmnt.createdAt.toDate().toLocaleString('id-ID', { hour: '2-digit', minute: '2-digit' })} WIB
                                        </p>
                                    </div>
                                    <div className="flex flex-col space-y-2 ml-3">
                                        <button 
                                            onClick={() => navigate('results', child, asmnt)}
                                            className="w-10 h-10 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center text-lg shadow-sm hover:bg-indigo-100 transition-colors"
                                        >
                                            👁️
                                        </button>
                                        <button 
                                            onClick={() => setShowHistoryActionSheet(asmnt.id)}
                                            className="w-10 h-10 bg-gray-50 text-gray-600 rounded-xl flex items-center justify-center text-lg shadow-sm hover:bg-gray-100 transition-colors"
                                        >
                                            ⚙️
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                );
        }
    };

    return (
        <div className="min-h-screen bg-[#F8FAFC] font-sans selection:bg-indigo-200 pb-28 relative">
            {/* Animated Background Mesh */}
            <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
                <div className="absolute top-[0%] left-[-10%] w-[50vw] h-[50vw] bg-indigo-200 rounded-full mix-blend-multiply filter blur-[100px] opacity-40 animate-blob"></div>
                <div className="absolute top-[30%] right-[-10%] w-[60vw] h-[60vw] bg-sky-200 rounded-full mix-blend-multiply filter blur-[100px] opacity-30 animate-blob animation-delay-2000"></div>
                <div className="absolute bottom-[-10%] left-[20%] w-[70vw] h-[70vw] bg-purple-200 rounded-full mix-blend-multiply filter blur-[100px] opacity-30 animate-blob animation-delay-4000"></div>
                <div className="absolute inset-0 bg-white/40 backdrop-blur-[50px]"></div>
            </div>

            {showShareModal && <ShareChildModal user={user} child={child} closeModal={() => setShowShareModal(false)} />}
            {showChildActionSheet && <ActionSheet title="Opsi Anak" options={getChildActionOptions()} onCancel={() => setShowChildActionSheet(false)} />}
            {showHistoryActionSheet && <ActionSheet title="Opsi Riwayat" options={historyActionOptions(showHistoryActionSheet)} onCancel={() => setShowHistoryActionSheet(null)} />}
            
            <div className="relative z-10">
                {/* Header Profile Section */}
                <div className="pt-8 px-5 pb-6 bg-white/30 backdrop-blur-md border-b border-white shadow-sm rounded-b-[2.5rem] mb-6">
                    {error && (
                        <div className="mb-4 bg-rose-50 border border-rose-200 text-rose-600 p-3 rounded-xl text-sm font-medium text-center">
                            {error}
                        </div>
                    )}
                    
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <div className="w-16 h-16 bg-gradient-to-tr from-indigo-500 to-purple-500 rounded-[1.25rem] flex items-center justify-center text-white text-2xl font-extrabold shadow-lg shadow-indigo-200 border-2 border-white">
                                {child.name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                                <h2 className="text-2xl font-extrabold text-gray-800 tracking-tight leading-tight">{child.name}</h2>
                                {(child.schoolName || child.className) && (
                                    <p className="text-[11px] text-gray-500 font-medium mt-0.5">
                                        {child.schoolName || 'PAUD Tidak Diketahui'} {child.className ? `• Kelas ${child.className}` : ''}
                                    </p>
                                )}
                                <p className="text-xs font-bold text-indigo-500 mt-1 uppercase tracking-widest">{calculateAge(child.dob.toDate())}</p>
                                <p className="text-xs text-gray-500 mt-0.5 capitalize">{child.gender}</p>
                            </div>
                        </div>
                        <div className="flex flex-col space-y-2">
                            <button onClick={() => setShowChildActionSheet(true)} className="w-10 h-10 bg-white/80 rounded-xl flex items-center justify-center text-xl shadow-sm border border-gray-100 hover:bg-gray-50 transition-colors">
                                ⚙️
                            </button>
                            <button onClick={() => setShowShareModal(true)} className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center text-xl shadow-sm border border-indigo-100 hover:bg-indigo-100 transition-colors">
                                🔗
                            </button>
                        </div>
                    </div>
                    
                    <Button 
                        onClick={() => navigate('assessmentHub', child)} 
                        className="w-full bg-gray-900 text-white hover:bg-gray-800 shadow-xl py-4 rounded-xl mt-6 transition-all font-bold"
                    >
                        <span className="mr-2">⚡</span> Mulai Pemeriksaan Baru
                    </Button>
                    
                    {user.role === 'Tenaga Kesehatan' && (
                        <Button 
                            onClick={() => navigate('referral', child)} 
                            className="w-full bg-rose-600 text-white hover:bg-rose-700 shadow-xl py-4 rounded-xl mt-3 transition-all font-bold"
                        >
                            <span className="mr-2">🚨</span> Buat Surat Rujukan Medis
                        </Button>
                    )}
                </div>

                <div className="px-5 space-y-6">
                    {activeIntervention && (
                        <div className="bg-white/60 backdrop-blur-2xl rounded-[1.5rem] p-1 border border-white shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
                            <InterventionPlanCard user={user} child={child} plan={activeIntervention} />
                        </div>
                    )}

                    <div className="bg-gradient-to-r from-sky-400 to-indigo-500 rounded-[1.5rem] p-[2px] shadow-lg shadow-indigo-200">
                        <button 
                            onClick={() => navigate('reportCard', child, { assessments, immunizations })} 
                            className="w-full bg-white rounded-[1.4rem] py-4 px-4 flex items-center justify-between transition-all hover:bg-gray-50"
                        >
                            <div className="flex items-center space-x-3">
                                <span className="text-2xl">🏆</span>
                                <span className="font-extrabold text-gray-800">Rapor Tumbuh Kembang</span>
                            </div>
                            <span className="text-gray-400">➔</span>
                        </button>
                    </div>

                    {/* Tabs Navigation */}
                    <div className="bg-white/50 backdrop-blur-xl p-1.5 rounded-[1.5rem] border border-white shadow-sm flex items-center">
                        <TabButton label="Riwayat" icon="📋" isActive={activeTab === 'history'} onClick={() => setActiveTab('history')} />
                        <TabButton label="Grafik" icon="📈" isActive={activeTab === 'chart'} onClick={() => setActiveTab('chart')} />
                        <TabButton label="Imunisasi" icon="💉" isActive={activeTab === 'immunization'} onClick={() => setActiveTab('immunization')} />
                        <TabButton label="Catatan" icon="📒" isActive={activeTab === 'notes'} onClick={() => setActiveTab('notes')} />
                    </div>
                    
                    {/* Tab Content */}
                    <div className="mt-6">
                        {renderTabContent()}
                    </div>
                </div>
            </div>

            <style>{`
                @keyframes blob {
                    0% { transform: translate(0px, 0px) scale(1); }
                    33% { transform: translate(30px, -50px) scale(1.1); }
                    66% { transform: translate(-20px, 20px) scale(0.9); }
                    100% { transform: translate(0px, 0px) scale(1); }
                }
                .animate-blob { animation: blob 15s infinite alternate ease-in-out; }
                .animation-delay-2000 { animation-delay: 2s; }
                .animation-delay-4000 { animation-delay: 4s; }
            `}</style>
        </div>
    );
}
